import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Select,
  InputNumber,
  Typography,
  Space,
  Tag,
  Progress,
  Statistic,
  message,
  Alert,
  Table,
  Modal,
  Divider
} from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  FireOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { Pie, Column } from '@ant-design/plots';
import quizAPI from '../../services/quizAPI';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

/**
 * 答题控制面板
 */
const QuizControl = () => {
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [duration, setDuration] = useState(60); // 答题时长(秒)
  const [currentSession, setCurrentSession] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  const timerRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    loadQuestions();
    loadClasses();

    // 如果从题库管理页面跳转过来，预选题目
    if (location.state?.question) {
      setSelectedQuestion(location.state.question);
    }

    return () => {
      // 清理定时器
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentSession) {
      startPolling();
    } else {
      stopPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession]);

  // 加载题目列表
  const loadQuestions = async () => {
    try {
      const response = await quizAPI.getQuestions({ includeHidden: false });
      if (response.data.success) {
        setQuestions(response.data.data || []);
      }
    } catch (error) {
      console.error('加载题目列表失败:', error);
    }
  };

  // 加载班级列表
  const loadClasses = async () => {
    try {
      const response = await api.get('/classes');
      if (response.data.success) {
        setClasses(response.data.data || []);
        // 默认选择第一个班级
        if (response.data.data.length > 0) {
          setSelectedClass(response.data.data[0].id);
        }
      }
    } catch (error) {
      console.error('加载班级列表失败:', error);
    }
  };

  // 开始答题
  const handleStart = async () => {
    if (!selectedQuestion) {
      message.error('请选择题目');
      return;
    }
    if (!selectedClass) {
      message.error('请选择班级');
      return;
    }

    try {
      setLoading(true);

      const response = await quizAPI.startSession({
        questionId: selectedQuestion.id,
        classId: selectedClass,
        mode: 'esp32',
        duration: duration
      });

      if (response.data.success) {
        const session = response.data.data;
        setCurrentSession(session);
        setRemainingTime(duration);
        message.success('答题已开始！');

        // 启动倒计时
        if (duration > 0) {
          startCountdown(duration);
        }
      } else {
        message.error(response.data.message || '开始失败');
      }
    } catch (error) {
      console.error('开始答题失败:', error);
      message.error(error.response?.data?.message || '开始失败');
    } finally {
      setLoading(false);
    }
  };

  // 结束答题
  const handleStop = async () => {
    if (!currentSession) return;

    try {
      setLoading(true);

      const response = await quizAPI.stopSession(currentSession.sessionId);

      if (response.data.success) {
        message.success('答题已结束！');
        setCurrentSession(null);
        setStatistics(response.data.data.statistics);
        
        // 停止倒计时
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        // 加载详细答题记录
        loadSessionDetail(currentSession.sessionId);
      } else {
        message.error('结束失败');
      }
    } catch (error) {
      console.error('结束答题失败:', error);
      message.error('结束失败');
    } finally {
      setLoading(false);
    }
  };

  // 启动倒计时
  const startCountdown = (seconds) => {
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          // 时间到，自动结束
          clearInterval(timerRef.current);
          timerRef.current = null;
          handleStop();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 启动轮询（获取实时统计）
  const startPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

    pollIntervalRef.current = setInterval(async () => {
      if (!currentSession) return;

      try {
        const response = await quizAPI.getStatistics(currentSession.sessionId);
        if (response.data.success) {
          setStatistics(response.data.data);
        }
      } catch (error) {
        console.error('获取统计数据失败:', error);
      }
    }, 2000); // 每2秒更新一次
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  // 加载会话详情
  const loadSessionDetail = async (sessionId) => {
    try {
      const response = await quizAPI.getSession(sessionId);
      if (response.data.success) {
        setAnswers(response.data.data.answers || []);
        setDetailModalVisible(true);
      }
    } catch (error) {
      console.error('加载会话详情失败:', error);
    }
  };

  // 格式化时间
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 文本清洗：避免带有花括号等特殊字符导致图表解析异常
  const sanitizeLabel = (text = '') => String(text).replace(/[{}]/g, '');

  // 准备图表数据
  const getPieData = () => {
    if (!statistics || !statistics.options) return [];

    return Object.values(statistics.options)
      .filter(opt => opt.count > 0)
      .map(opt => ({
        type: `${opt.key}. ${sanitizeLabel(opt.label)}`,
        value: opt.count
      }));
  };

  const getColumnData = () => {
    if (!statistics || !statistics.options) return [];

    return Object.values(statistics.options).map(opt => ({
      option: opt.key,
      count: opt.count,
      percentage: opt.percentage,
      label: sanitizeLabel(opt.label)
    }));
  };

  // 答题记录表格列
  const answerColumns = [
    {
      title: '序号',
      key: 'index',
      width: 60,
      render: (_, __, index) => index + 1
    },
    {
      title: '答案',
      dataIndex: 'answer',
      key: 'answer',
      width: 80,
      render: (text) => <Tag color="blue" style={{ fontSize: 16 }}>{text}</Tag>
    },
    {
      title: '设备',
      dataIndex: 'device_id',
      key: 'device_id',
      render: (text) => text ? `ESP32-${text.substring(6)}` : '-'
    },
    {
      title: '学生',
      key: 'student',
      render: (_, record) => {
        if (record.student_name) {
          return `${record.student_name} (${record.student_number})`;
        }
        return <Tag>未绑定</Tag>;
      }
    },
    {
      title: '答题时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString('zh-CN')
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Title level={3}>
          <FireOutlined /> 答题控制面板
        </Title>
        <Paragraph type="secondary">
          选择题目和班级，启动ESP32红外遥控器答题，实时查看统计结果。
        </Paragraph>

        <Divider />

        {/* 控制区域 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card size="small" title="选择题目">
              <Select
                style={{ width: '100%' }}
                placeholder="请选择题目"
                value={selectedQuestion?.id}
                onChange={(value) => {
                  const question = questions.find(q => q.id === value);
                  setSelectedQuestion(question);
                }}
                disabled={currentSession !== null}
              >
                {questions.map(q => (
                  <Option key={q.id} value={q.id}>
                    {q.title.substring(0, 50)}...
                  </Option>
                ))}
              </Select>
              {selectedQuestion && (
                <div style={{ marginTop: 12 }}>
                  <Tag color={selectedQuestion.type === 'single' ? 'blue' : 'orange'}>
                    {selectedQuestion.type === 'single' ? '单选题' : '评分题'}
                  </Tag>
                  <Tag>{selectedQuestion.options?.length || 0} 个选项</Tag>
                </div>
              )}
            </Card>
          </Col>

          <Col span={8}>
            <Card size="small" title="选择班级">
              <Select
                style={{ width: '100%' }}
                placeholder="请选择班级"
                value={selectedClass}
                onChange={setSelectedClass}
                disabled={currentSession !== null}
              >
                {classes.map(cls => (
                  <Option key={cls.id} value={cls.id}>{cls.name}</Option>
                ))}
              </Select>
            </Card>
          </Col>

          <Col span={8}>
            <Card size="small" title="答题时长(秒)">
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={600}
                value={duration}
                onChange={setDuration}
                disabled={currentSession !== null}
                placeholder="0表示不限时"
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                0表示不限时，手动结束
              </Text>
            </Card>
          </Col>
        </Row>

        {/* 操作按钮 */}
        <Row justify="center" style={{ marginBottom: 24 }}>
          <Space size="large">
            {!currentSession ? (
              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                onClick={handleStart}
                loading={loading}
              >
                开始答题
              </Button>
            ) : (
              <>
                <Button
                  danger
                  size="large"
                  icon={<PauseCircleOutlined />}
                  onClick={handleStop}
                  loading={loading}
                >
                  结束答题
                </Button>
                {duration > 0 && (
                  <Statistic
                    title="剩余时间"
                    value={formatTime(remainingTime)}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: remainingTime < 10 ? '#cf1322' : '#3f8600' }}
                  />
                )}
              </>
            )}
          </Space>
        </Row>

        {/* 当前状态 */}
        {currentSession && (
          <Alert
            message="答题进行中"
            description={`题目: ${selectedQuestion?.title || ''} | 班级: ${classes.find(c => c.id === selectedClass)?.name || ''}`}
            type="success"
            showIcon
            icon={<FireOutlined />}
            style={{ marginBottom: 24 }}
          />
        )}

        {/* 统计数据 */}
        {statistics && (
          <>
            <Divider>实时统计</Divider>

            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic
                  title="已答题人数"
                  value={statistics.total || 0}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="参与率"
                  value={statistics.total || 0}
                  suffix="/ ?"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Card title="选项分布 - 饼图" size="small">
                  <Pie
                    data={getPieData()}
                    angleField="value"
                    colorField="type"
                    radius={0.8}
                    label={{
                      type: 'outer',
                      content: '{name} {percentage}'
                    }}
                    interactions={[
                      { type: 'element-active' }
                    ]}
                    height={300}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card title="选项分布 - 柱状图" size="small">
                  <Column
                    data={getColumnData()}
                    xField="option"
                    yField="count"
                    label={{
                      position: 'top',
                      style: {
                        fill: '#000',
                        opacity: 0.6
                      }
                    }}
                    xAxis={{
                      label: {
                        autoHide: false,
                        autoRotate: false
                      }
                    }}
                    height={300}
                  />
                </Card>
              </Col>
            </Row>

            <Card title="详细统计" size="small" style={{ marginTop: 16 }}>
              <Table
                dataSource={Object.values(statistics.options || {})}
                rowKey="key"
                size="small"
                pagination={false}
                columns={[
                  {
                    title: '选项',
                    dataIndex: 'key',
                    key: 'key',
                    render: (text) => <Tag color="blue">{text}</Tag>
                  },
                  {
                    title: '内容',
                    dataIndex: 'label',
                    key: 'label'
                  },
                  {
                    title: '人数',
                    dataIndex: 'count',
                    key: 'count',
                    render: (text) => <strong>{text}</strong>
                  },
                  {
                    title: '占比',
                    dataIndex: 'percentage',
                    key: 'percentage',
                    render: (text, record) => (
                      <div>
                        <Progress percent={text} size="small" />
                      </div>
                    )
                  }
                ]}
              />
            </Card>
          </>
        )}
      </Card>

      {/* 答题详情对话框 */}
      <Modal
        title="答题详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        <Table
          columns={answerColumns}
          dataSource={answers}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Modal>
    </div>
  );
};

export default QuizControl;

